// frontend/src/api/analyticsService.ts
import apiClient from './index';
import {
  type TrafficOverviewKpis,
  type TrafficOverTimeResponse,
  type TopPagesResponse,
  type DemographicsResponse,
  // We don't directly use DateRangeQuery here, but pass its constituent parts
} from '@/types/analyticsTypes'; // Make sure analyticsTypes.ts is created and exported
import { type DateRange } from 'react-day-picker'; // For typing convenience

// Helper to format dates as YYYY-MM-DD strings
const formatDateForApi = (date: Date | undefined | null): string | undefined => {
  if (!date) return undefined;
  return date.toISOString().split('T')[0];
};

interface AnalyticsRequestParams {
  start_date: string;
  end_date: string;
  // Add other common params like 'dimension' if needed for specific calls
}

export const getTrafficOverviewKpis = async (
  dateRange: DateRange | undefined
): Promise<TrafficOverviewKpis> => {
  if (!dateRange?.from || !dateRange?.to) {
    throw new Error("Date range is required for fetching KPIs.");
  }
  const params: AnalyticsRequestParams = {
    start_date: formatDateForApi(dateRange.from)!,
    end_date: formatDateForApi(dateRange.to)!,
  };
  // Added trailing slash to the endpoint path
  const response = await apiClient.get<TrafficOverviewKpis>('/analytics/traffic-overview-kpis/', { params });
  return response.data;
};

export const getTrafficOverTime = async (
  dateRange: DateRange | undefined
): Promise<TrafficOverTimeResponse> => {
  if (!dateRange?.from || !dateRange?.to) {
    throw new Error("Date range is required for fetching traffic over time.");
  }
  const params: AnalyticsRequestParams = {
    start_date: formatDateForApi(dateRange.from)!,
    end_date: formatDateForApi(dateRange.to)!,
  };
  // Added trailing slash to the endpoint path
  const response = await apiClient.get<TrafficOverTimeResponse>('/analytics/traffic-over-time/', { params });
  return response.data;
};

export const getTopPages = async (
  dateRange: DateRange | undefined,
  limit: number = 10
): Promise<TopPagesResponse> => {
  if (!dateRange?.from || !dateRange?.to) {
    throw new Error("Date range is required for fetching top pages.");
  }
  const params: AnalyticsRequestParams & { limit: number } = {
    start_date: formatDateForApi(dateRange.from)!,
    end_date: formatDateForApi(dateRange.to)!,
    limit,
  };
  // Added trailing slash to the endpoint path
  const response = await apiClient.get<TopPagesResponse>('/analytics/top-pages/', { params });
  return response.data;
};

export const getDemographicsData = async (
  dateRange: DateRange | undefined,
  dimension: 'country' | 'deviceCategory' = 'country' // Example dimensions
): Promise<DemographicsResponse> => {
  if (!dateRange?.from || !dateRange?.to) {
    throw new Error("Date range is required for fetching demographics.");
  }
  const params: AnalyticsRequestParams & { dimension: string } = {
    start_date: formatDateForApi(dateRange.from)!,
    end_date: formatDateForApi(dateRange.to)!,
    dimension,
  };
  // Added trailing slash to the endpoint path
  const response = await apiClient.get<DemographicsResponse>('/analytics/demographics/', { params });
  return response.data;
};

// You'll also need to create/update frontend/src/types/analyticsTypes.ts
// based on the schemas in backend/app/schemas/analytics.py
